import { parseCCTPMessageAnyVersion } from "../version";
import { checkNonceUsedOnAptos } from "./aptos";
import { APTOS_CCTP_DOMAIN_ID, SOLANA_CCTP_DOMAIN_ID } from "./contracts";
import { checkNonceUsedOnEvm } from "./evm";
import { checkNonceUsedOnSolana } from "./solana";
import { checkV2NonceUsed, type ClaimStatus } from "./v2";

export type { ClaimStatus };

/**
 * Check if a CCTP nonce has been consumed on the destination chain.
 * Dispatches to the appropriate on-chain check based on destination domain.
 */
export async function checkNonceRedeemed(
  sourceDomain: number,
  nonce: number,
  destDomain: number,
): Promise<boolean> {
  if (destDomain === APTOS_CCTP_DOMAIN_ID) {
    return checkNonceUsedOnAptos(sourceDomain, nonce);
  }
  if (destDomain === SOLANA_CCTP_DOMAIN_ID) {
    return checkNonceUsedOnSolana(sourceDomain, nonce);
  }
  const evmResult = await checkNonceUsedOnEvm(sourceDomain, nonce, destDomain);
  return evmResult === true;
}

/**
 * Whether an attested CCTP message has been claimed on its destination, using the check for the
 * message's own protocol version. `unknown` also covers a message that doesn't parse.
 */
export async function checkMessageClaimed(messageHex: string): Promise<ClaimStatus> {
  const parsed = parseCCTPMessageAnyVersion(messageHex);
  if (!parsed) return "unknown";
  if (parsed.version === 2) return checkV2NonceUsed(parsed.nonce, parsed.destDomain);
  const used = await checkNonceRedeemed(parsed.sourceDomain, parsed.nonce, parsed.destDomain);
  return used ? "claimed" : "unclaimed";
}
